import { config, logger } from '@services/context';
import { dateService } from '@services/DateService';
import { useUserContext } from '@shared/contexts/UserContextProvider';
import { PermissionsEnum } from '@shared/protos/user';
import { useScreenSize } from '@shared/hooks/useScreenSize';
import { SettlementType } from '@shared/protos/settlementPrices';
import { ReactElement, useCallback, useMemo } from 'react';
import { Layout } from 'react-grid-layout';
import { WidgetType } from '../enums';
import { getDataGrid, layoutColumns, layoutRowSize, responsiveUtils } from '../layoutUtils';
import { DataGrid, Widget, WidgetPayload } from '../types';
import Chart from './chart/Chart';
import { FloatingChart } from './chart/FloatingChart';
import { LadderPayload } from './ladder/LadderTypes';
import { LadderWidget } from './ladder/LadderWidget';
import { NewsWidgetPayload } from './news/newsTypes';
import { NewsWidget } from './news/NewsWidget';
import Prices from './prices/Prices';
import { PricesWidgetPayload } from './prices/types';
import { Reports } from './reports/Reports';
import { ReportsWidgetPayload } from './reports/types';
import { SeasonalChart } from './seasonal-chart/SeasonalChart';
import SettlementPrices from './settlement-prices/SettlementPrices';
import { SettlementPricesWidgetPayload } from './settlement-prices/types';
import { usePricesDefaultFilters } from './shared/hooks/usePricesDefaultFilters';
import { TradingBlotter } from './trading-blotter/TradingBlotter';
import { TradingBlotterWidgetPayload } from './trading-blotter/tradingBlotterTypes';
import { TradingV2 } from './trading-v2/TradingV2';
import { TradingV2WidgetPayload } from './trading-v2/types';
import { VideoPayload } from './videos/types';
import { Videos } from './videos/Videos';
import { Watchlist } from './watchlist/Watchlist';
import { WatchlistPayload } from './watchlist/WatchlistTypes';
import { EmptyWidget } from './WidgetUtils';

//ICONS
import { ViewTimeline as LadderIcon, RemoveRedEye } from '@mui/icons-material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BalanceIcon from '@mui/icons-material/Balance';
import MultipleStopIcon from '@mui/icons-material/MultipleStop';
import NewsPaperIcon from '@mui/icons-material/Newspaper';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SSIDChartIcon from '@mui/icons-material/SsidChart';
import SummarizeIcon from '@mui/icons-material/Summarize';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import TableChartIcon from '@mui/icons-material/TableChart';
import TimeLineIcon from '@mui/icons-material/Timeline';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import { HorizontalSplit as OrderTrackerIcon } from '@mui/icons-material';
import { OrderTrackerWidget } from './order-tracker/OrderTrackerWidget';
import { OrderTrackerTabData } from './order-tracker/store/OrderTrackerTypes';

type WidgetDef = {
  type: WidgetType;
  displayName: string;
  displayOrder?: number;
  dataGrid?: DataGrid;
  defaultPayload?: WidgetPayload;
  hidden?: boolean;
  Component: (data: Widget, props: { isFrozen?: boolean }) => ReactElement;
  zIndex?: number;
  targetWidth?: number;
  targetHeight?: number;
};

type WidgetMenuItem = {
  type: WidgetType;
  displayName: string;
  defaultPayload?: WidgetPayload;
};

// Blank widget to render when another is unavailble; there's some very weird behaviour if
// findWidget returns null or undefined

const EmptyWidgeDef = {
  type: WidgetType.Empty,
  displayName: 'NULL WIDGET',
  Component: props => <EmptyWidget {...props}></EmptyWidget>,
};

// Ideally we'd be pulling this from a config file but this will do for now
export const DevOnlyWidgets: WidgetType[] = [WidgetType.Ladder];
export const UATOnlyWidgets: WidgetType[] = [WidgetType.OrderTracker];

export function useWidgets() {
  const { DEFAULT_ALLOWED_PRICING_PRODUCTS, DEFAULT_ALLOWED_PRICING_TENORS, DEFAULT_ALLOWED_TRADING_TENORS } = usePricesDefaultFilters();
  const { isMobile, isMobileLandscape } = useScreenSize();
  const user = useUserContext();

  const widgets: WidgetDef[] = useMemo(
    () =>
      [
        {
          type: WidgetType.Pricing,
          displayName: 'Prices',
          displayOrder: 1,
          defaultPayload: {
            selectedColumns: DEFAULT_ALLOWED_PRICING_PRODUCTS,
            selectedRows: [],
            columnsOrder: [],
            userRollingRowSettings: DEFAULT_ALLOWED_PRICING_TENORS,
            isOverrideRolling: false,
          } as PricesWidgetPayload,
          Component: (widget, props) => <Prices {...widget} {...props} title="Prices" />,
          hidden: !user?.access_live_data && !user?.permissions?.includes(PermissionsEnum.BROKER),
          zIndex: 21,
        },
        {
          type: WidgetType.Chart,
          displayName: 'Chart',
          displayOrder: 2,
          Component: props => <Chart {...props} title="Chart" />,
          hidden: !user?.access_live_data || !user?.access_historical_data,
          zIndex: 20,
        },
        {
          type: WidgetType.Settlements,
          displayName: 'Flux Snapshots',
          displayOrder: 3,
          defaultPayload: {
            settlementDate: dateService.getPreviousWorkday(),
            selectedSettlementTypes: [SettlementType.Flux],
            selectedColumns: DEFAULT_ALLOWED_PRICING_PRODUCTS,
            selectedRows: [],
            userRollingRowSettings: DEFAULT_ALLOWED_PRICING_TENORS,
            isOverrideRolling: false,
            columnsOrder: [],
          } as SettlementPricesWidgetPayload,
          Component: props => <SettlementPrices {...props} title="Flux Snapshots" />,
          hidden: !user?.snapshot_periods.length,
          zIndex: 20,
        },
        {
          type: WidgetType.FloatingChart,
          displayName: 'Floating Chart',
          displayOrder: 4,
          hidden: true,
          Component: props => <FloatingChart {...props} title="Floating Chart" />,
          zIndex: 20,
        },
        {
          type: WidgetType.TradingBlotter,
          displayName: 'Trading Blotter',
          displayOrder: 6,
          defaultPayload: {
            hiddenColumns: [],
          } as TradingBlotterWidgetPayload,
          Component: (widget, props) => <TradingBlotter {...widget} {...props} title="Trading Blotter" />,
          hidden: isMobile || isMobileLandscape || !user?.allow_trading,
          zIndex: 20,
        },
        {
          type: WidgetType.News,
          displayName: 'News',
          displayOrder: 7,
          defaultPayload: {} as NewsWidgetPayload,
          Component: (widget, props) => <NewsWidget {...widget} {...props} title="News" />,
          zIndex: 20,
        },
        {
          type: WidgetType.SeasonalChart,
          displayName: 'Seasonal Chart',
          displayOrder: 8,
          Component: props => <SeasonalChart {...props} title="Seasonal Chart" />,
          hidden: user?.permissions?.includes(PermissionsEnum.BROKER),
          zIndex: 20,
        },
        {
          type: WidgetType.TradingV2,
          displayName: 'Trading V2',
          displayOrder: 9,
          defaultPayload: {} as TradingV2WidgetPayload,
          Component: (widget, props) => <TradingV2 {...widget} {...props} title="" />,
          hidden: isMobile || isMobileLandscape || !user?.allow_trading || user?.trade_limits?.length === 0,
          zIndex: 20,
        },
        {
          type: WidgetType.Reports,
          displayName: 'Reports',
          displayOrder: 10,
          defaultPayload: {} as ReportsWidgetPayload,
          Component: props => <Reports {...props} title="Reports" />,
          hidden: user?.research_reports.length === 0,
          zIndex: 20,
        },
        {
          type: WidgetType.Videos,
          displayName: 'Flux Media',
          displayOrder: 11,
          defaultPayload: {} as VideoPayload,
          Component: props => <Videos {...props} title="Flux Media" />,
        },
        {
          type: WidgetType.WatchList,
          displayName: 'Watchlist',
          displayOrder: 12,
          defaultPayload: { records: [] } as WatchlistPayload,
          Component: props => <Watchlist {...props} title="Watchlist" />,
        },
        {
          type: WidgetType.Ladder,
          displayName: 'Ladder',
          displayOrder: 13,
          defaultPayload: { tabs: [{ symbol: null, tenorCode: null, exchange: null }], lastVisibleTab: 0 } as LadderPayload,
          Component: props => <LadderWidget {...props} title="Ladder" />,
        },
        {
          type: WidgetType.OrderTracker,
          displayName: 'Order View',
          displayOrder: 14,
          defaultPayload: { tabs: [] as OrderTrackerTabData[], tabIndex: 0 },
          Component: props => <OrderTrackerWidget {...props} title="Order View" />,
        },
      ].filter(def => {
        if (config.isProd) {
          if (DevOnlyWidgets.includes(def.type) || UATOnlyWidgets.includes(def.type)) return false;
        } else if (config.isUAT && DevOnlyWidgets.includes(def.type)) return false;
        return true;
      }),
    [DEFAULT_ALLOWED_PRICING_PRODUCTS, DEFAULT_ALLOWED_PRICING_TENORS, DEFAULT_ALLOWED_TRADING_TENORS, user]
  );

  const menuItems: WidgetMenuItem[] = useMemo(
    () =>
      widgets
        .filter(w => !w.hidden)
        .slice()
        .sort((a, b) => (a.displayOrder || Infinity) - (b.displayOrder || Infinity))
        .map(def => ({
          type: def.type,
          displayName: def.displayName,
          defaultPayload: def.defaultPayload,
        })),
    [widgets]
  );

  // TODO: find a less insane way of sizing things- widget size should be based on content,
  // and some widgets are definitely going to break if the page size is too big or too small

  // For reference: breakpoint values are: { lg: 1900, md: 1000, sm: 600, xs: 0 };

  // Temporary solution: calculate the number of columns and rows to a particular size

  const getNearestGridSizeForWidget = (
    type: WidgetType,
    breakpoint: string,
    targetWidth: number,
    targetHeight: number,
    applyMin: boolean = false,
    roundUp: boolean = false
  ) => {
    const numCols = responsiveUtils.getColsFromBreakpoint(breakpoint, layoutColumns);
    const pageWidth = window.innerWidth;

    const colSize = pageWidth / numCols;
    const rowSize = layoutRowSize;
    let w = targetWidth / colSize;
    let h = targetHeight / rowSize;

    if (roundUp) {
      w = Math.ceil(w);
      h = Math.ceil(h);
    } else {
      w = Math.round(w);
      h = Math.round(h);
    }

    if (applyMin) {
      return { w, h, minW: w, minH: h };
    }

    return { w, h };
  };

  const getOverridenWidgetMinDimensions = useCallback((type: WidgetType, breakpoint: string) => {
    switch (type) {
      case WidgetType.News:
        return { minW: 1, minH: 3 };
      case WidgetType.TradingV2:
        return { w: 3, h: 8, minW: 3, minH: 7, maxW: 4, maxH: 8 };
      case WidgetType.Pricing:
        return { minW: 6, minH: 6, w: 6, h: 14 };
      case WidgetType.Reports:
        return { w: 14 };
      case WidgetType.Videos:
        return { w: 9, h: 16 };
      case WidgetType.WatchList:
        return getNearestGridSizeForWidget(WidgetType.WatchList, breakpoint, 850, 350);
      case WidgetType.Ladder:
        return getNearestGridSizeForWidget(WidgetType.Ladder, breakpoint, 850, 680, false, false);
      case WidgetType.OrderTracker:
        return getNearestGridSizeForWidget(WidgetType.OrderTracker, breakpoint, 1000, 550, false, true);
      default:
        return {};
    }
  }, []);

  const findWidget = useCallback(
    ({ id, type }: Widget, breakpoint: string, widgetLayout: Layout | undefined, widgetIndex?: number): WidgetDef | undefined => {
      let widget = widgets.find(w => w.type === type);

      if (!widget) {
        logger.error(`Widget type '${type}' does not exist`);
        widget = EmptyWidgeDef;
      }

      const dataGrid = { ...getDataGrid(breakpoint), ...getOverridenWidgetMinDimensions(widget.type, breakpoint), i: id };

      if (!widgetLayout || isMobile) {
        return {
          ...widget,
          dataGrid: {
            ...dataGrid,
            x: 0,
          },
        };
      }

      return {
        ...widget,
        dataGrid: { ...dataGrid, ...widgetLayout },
      };
    },
    [widgets, getOverridenWidgetMinDimensions, isMobile]
  );

  const getWidgetIcon = (type: WidgetType) => {
    switch (type) {
      case WidgetType.Chart:
        return <ShowChartIcon />;
      case WidgetType.FloatingChart:
        return <TimeLineIcon />;
      case WidgetType.News:
        return <NewsPaperIcon />;
      case WidgetType.Settlements:
        return <BalanceIcon />;
      case WidgetType.SeasonalChart:
        return <SSIDChartIcon />;
      case WidgetType.Trading:
        return <MultipleStopIcon />;
      case WidgetType.TradingV2:
        return <SwapHorizIcon />;
      case WidgetType.Pricing:
        return <TableChartIcon />;
      case WidgetType.TradingBlotter:
        return <AccountBalanceIcon />;
      case WidgetType.Reports:
        return <SummarizeIcon />;
      case WidgetType.Videos:
        return <VideoLibraryIcon />;
      case WidgetType.WatchList:
        return <RemoveRedEye />;
      case WidgetType.Ladder:
        return <LadderIcon />;
      case WidgetType.OrderTracker:
        return <OrderTrackerIcon />;
      default:
        return null;
    }
  };
  return { menuItems, findWidget, getWidgetIcon };
}
